/**
 * iter 73.10+ — AnnouncementSchedule
 *
 * Compact Gantt-style month timeline for org announcements. Renders
 * above the announcement list so admins can spot dogpiles (three
 * critical banners on the same Monday morning is a bad look).
 *
 * Semantics:
 *   • X-axis = days of the currently-viewed month.
 *   • Each announcement = one thin row. Bar spans [max(created_at,
 *     starts_at) .. ends_at || month-end]. Unbounded ends are drawn
 *     with a chevron on the right edge.
 *   • Bar colour = priority palette (info/warning/critical) — matches
 *     AnnouncementBanner exactly for visual continuity.
 *   • Overlap heatmap: a thin strip above the grid tallies concurrent
 *     active announcements per day; cells with >1 get a red hairline.
 *
 * The component takes only what it needs — pure presentational, no
 * fetching. Click a bar → invokes onSelect(announcement) so the parent
 * opens the edit drawer with the row.
 */
import { useMemo, useState } from "react";
import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";

const PRIORITY_BAR = {
  info:     "bg-blue-500/80 hover:bg-blue-500 text-white",
  warning:  "bg-amber-500/80 hover:bg-amber-500 text-white",
  critical: "bg-red-500/80 hover:bg-red-500 text-white",
};

const monthLabel = (d) =>
  d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const daysInMonth = (d) => endOfMonth(d).getDate();

const clampDay = (dt, monthStart, monthEnd) => {
  const t = dt.getTime();
  if (t < monthStart.getTime()) return 0;
  if (t > monthEnd.getTime()) return daysInMonth(monthStart);
  return dt.getDate();
};

export default function AnnouncementSchedule({ announcements = [], onSelect }) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const totalDays = daysInMonth(monthStart);
  const today = new Date();
  const isCurrentMonth =
    today.getFullYear() === monthStart.getFullYear() &&
    today.getMonth() === monthStart.getMonth();

  // Compute the bar geometry (in day-percent) for each announcement.
  // Uses created_at as the effective start when starts_at is null.
  const bars = useMemo(() => {
    return announcements.map((a) => {
      const effStart = new Date(a.starts_at || a.created_at || monthStart);
      const effEnd = a.ends_at ? new Date(a.ends_at) : null;
      const visibleInMonth =
        effStart <= monthEnd && (!effEnd || effEnd >= monthStart);
      if (!visibleInMonth) return null;

      const startDay = clampDay(effStart, monthStart, monthEnd);
      const endDay = effEnd ? clampDay(effEnd, monthStart, monthEnd) : totalDays;
      // If starts_at falls before this month, show a chevron on the left.
      const overflowLeft = effStart < monthStart;
      // If ends_at is null or after month-end → chevron on the right.
      const overflowRight = !effEnd || effEnd > monthEnd;

      const leftPct = ((Math.max(startDay, 1) - 1) / totalDays) * 100;
      const widthPct = ((endDay - Math.max(startDay, 1) + 1) / totalDays) * 100;

      return {
        a,
        leftPct,
        widthPct: Math.max(widthPct, 100 / totalDays), // min 1 day wide
        overflowLeft,
        overflowRight,
      };
    });
  }, [announcements, monthStart, monthEnd, totalDays]);

  // Per-day active count for the overlap heatmap strip.
  const perDayCount = useMemo(() => {
    const counts = Array(totalDays).fill(0);
    announcements.forEach((a) => {
      const s = new Date(a.starts_at || a.created_at || monthStart);
      const e = a.ends_at ? new Date(a.ends_at) : monthEnd;
      for (let d = 1; d <= totalDays; d++) {
        const day = new Date(monthStart.getFullYear(), monthStart.getMonth(), d, 12);
        if (day >= s && day <= e) counts[d - 1] += 1;
      }
    });
    return counts;
  }, [announcements, monthStart, monthEnd, totalDays]);

  const maxOverlap = Math.max(1, ...perDayCount);
  const visibleBars = bars.filter(Boolean);

  const goPrev = () =>
    setCursor(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1));
  const goNext = () =>
    setCursor(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1));
  const goToday = () => setCursor(startOfMonth(new Date()));

  if (!announcements.length) return null;

  return (
    <div
      className="mb-3 rounded border border-border bg-muted/20 p-3"
      data-testid="announcement-schedule"
    >
      {/* Header: month navigation */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <CalendarBlank size={13} className="text-muted-foreground" />
          <span className="text-[11px] mono uppercase tracking-wide text-muted-foreground">
            Schedule
          </span>
          <span
            className="text-sm font-semibold ml-2"
            data-testid="announcement-schedule-month"
          >
            {monthLabel(monthStart)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={goPrev}
            title="Previous month"
            data-testid="announcement-schedule-prev"
          >
            <CaretLeft size={12} />
          </button>
          {!isCurrentMonth && (
            <button
              type="button"
              className="btn btn-ghost btn-xs text-[10px]"
              onClick={goToday}
              data-testid="announcement-schedule-today"
            >
              TODAY
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={goNext}
            title="Next month"
            data-testid="announcement-schedule-next"
          >
            <CaretRight size={12} />
          </button>
        </div>
      </div>

      {/* Day-scale axis + overlap heatmap strip */}
      <div className="relative">
        <div className="flex text-[9px] mono text-muted-foreground mb-0.5">
          {Array.from({ length: totalDays }, (_, i) => i + 1).map((d) => (
            <div
              key={d}
              className="flex-1 text-center"
              style={{ minWidth: 0 }}
            >
              {/* Only show every 5th label + last day, to avoid clutter. */}
              {d === 1 || d % 5 === 0 || d === totalDays ? d : ""}
            </div>
          ))}
        </div>
        {/* Overlap heatmap: 1 = neutral, >1 = warning tint */}
        <div className="flex h-1 mb-1" data-testid="announcement-schedule-heatmap">
          {perDayCount.map((n, i) => {
            const intensity = maxOverlap > 1 ? n / maxOverlap : 0;
            const cls =
              n <= 1
                ? "bg-transparent"
                : n === 2
                ? "bg-amber-500/40"
                : "bg-red-500/60";
            return (
              <div
                key={i}
                className={`flex-1 ${cls}`}
                title={`Day ${i + 1}: ${n} active`}
                style={{ opacity: n > 1 ? 0.4 + intensity * 0.6 : 0 }}
              />
            );
          })}
        </div>
        {/* Today marker (only when viewing current month) */}
        {isCurrentMonth && (
          <div
            className="absolute top-3 bottom-0 w-px bg-primary/60 z-10 pointer-events-none"
            style={{ left: `${((today.getDate() - 0.5) / totalDays) * 100}%` }}
            data-testid="announcement-schedule-today-marker"
          />
        )}

        {/* Gantt rows */}
        <div className="space-y-1">
          {visibleBars.map(({ a, leftPct, widthPct, overflowLeft, overflowRight }) => {
            const preview = (a.content_html || "").replace(/<[^>]*>/g, "").slice(0, 60);
            return (
              <div
                key={a.id}
                className="relative h-5"
                data-testid={`announcement-schedule-row-${a.id}`}
              >
                <button
                  type="button"
                  onClick={() => onSelect?.(a)}
                  className={`absolute top-0 h-full rounded-sm text-[10px] leading-5 px-1.5 truncate transition-colors ${
                    PRIORITY_BAR[a.priority] || PRIORITY_BAR.info
                  }`}
                  style={{
                    left: `${leftPct}%`,
                    width: `calc(${widthPct}% - 2px)`,
                  }}
                  title={`${a.priority.toUpperCase()} — ${preview}`}
                  data-testid={`announcement-schedule-bar-${a.id}`}
                >
                  {overflowLeft && <span className="mr-0.5">‹</span>}
                  {preview || "(empty)"}
                  {overflowRight && <span className="ml-0.5">›</span>}
                </button>
              </div>
            );
          })}
          {visibleBars.length === 0 && (
            <div className="text-[11px] text-muted-foreground italic text-center py-2">
              No announcements scheduled this month.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
